import { Fragment, useEffect, useState } from "react"
import Modal from "@vapor/react-material/Modal"
import Button from "@vapor/react-material/Button"
import Tooltip from "@vapor/react-material/Tooltip"
import Box from "@vapor/react-material/Box"
import Stack from "@vapor/react-material/Stack"
import Divider from "@vapor/react-material/Divider"
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { modalStyleXLarge } from "../../businessLogic/styles"
import { useSnackbar } from "notistack"
import { useTranslation } from "@1f/react-sdk"
import { Print as PrintIcon } from "@mui/icons-material";
import { TextField } from "@vapor/react-material"
import { LogApi } from "../../businessLogic/api/logApi"
import { PorcedureSosApi } from "../../businessLogic/api/porceduraSosApi"
import { UserRolesApi } from "../../businessLogic/api/userRolesApi"
import { Loading } from "../../components/Loading"
import { ModalPdfViewer } from "../../components/ModalPdfViewer"

export const ModalManageSosProcedure = ({ show, onClose, procedureFromParent, tenantId, aziendaId, loggedUserId }) => {
    
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const logApi = LogApi();
    const porcedureSosApi = PorcedureSosApi();   
    const userRolesApi = UserRolesApi();     
    const [procedureSos, setProcedureSos] = useState(procedureFromParent);    
    const [isUserReferenteSos, setIsUserReferenteSos] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [pdfViewerTitle, setPdfViewerTitle] = useState("");
    const [pdfViewerBase64, setPdfViewerBase64] = useState(null);

    useEffect(()=>{
        if(loggedUserId){
            checkIfUserIsReferenteSos();
        }
    }, [loggedUserId]);

    async function checkIfUserIsReferenteSos() {
        let isReferente = false;
        setIsLoading(true);
        try {
            let userRoleOfLoggedUser = await userRolesApi.GetUserRolesDetails(loggedUserId)
            if(userRoleOfLoggedUser && userRoleOfLoggedUser.length > 0){
                let sosReferenteRole = userRoleOfLoggedUser.find(role => role.role === "sos_referente");
                if(sosReferenteRole){
                    isReferente = true;
                }
            }
        } catch (error) {
            logApi.AddLog(`"Errore durante la verifica del ruolo di referente SOS per l'utente: ${loggedUserId}, errore: ${error}"`,error.stack,"checkIfUserIsReferenteSos","ModalManageSosProcedure");
            enqueueSnackbar("Errore durante la verifica del ruolo di referente SOS", { variant: "error" });            
        }
        finally{
            setIsUserReferenteSos(isReferente);
            setIsLoading(false);
        }
    }

    const handleChangeFields = (event) => {
        let { id, value } = event.target;
        switch(id){
            case "txtNominativoReferenteSos":
                setProcedureSos({ ...procedureSos, nominativoReferenteSos: value });
                break;            
            case "txtContributoColleghi":
                setProcedureSos({ ...procedureSos, contributoColleghi: value });
                break;
            case "txtAttivitaStrumentiAnomalie":
                setProcedureSos({ ...procedureSos, attivitaStrumentiAnomalie: value });
                break;
            case "txtPrioritaAnomalie":
                setProcedureSos({ ...procedureSos, prioritaAnomalie: value });
                break;
            case "txtStrumentiSelezione":
                setProcedureSos({ ...procedureSos, strumentiSelezione: value });
                break;
            case "txtPartenariati":
                setProcedureSos({ ...procedureSos, partenariati: value });
                break;
            case "txtSoggettiEsterni":
                setProcedureSos({ ...procedureSos, soggettiEsterni: value });
                break;    
        }
    }

    const handleModalClose = () => {
        onClose();
    }
    
    const handleSave = async () => {
        try {
            setIsLoading(true);            
            let response = await porcedureSosApi.PostProcedureSos(tenantId, aziendaId, procedureSos)            
            if(response){
                enqueueSnackbar("Procedura SOS salvata con successo", { variant: "success" });
                setProcedureSos(response);                
            }
        } catch (error) {
            logApi.AddLog(`"Errore durante il salvataggio della procedura SOS per l'azienda: ${aziendaId}, errore: ${error}"`,error.stack,"handleSave","ModalManageSosProcedure");
            enqueueSnackbar("Errore durante il salvataggio della procedura SOS", { variant: "error" });            
        }
        finally{
            setIsLoading(false);
        }
    }

    const handlePrintReport = async () => {
        try {
            setIsLoading(true);
            let reportResponse = await porcedureSosApi.GetProcedureSosReport(aziendaId, loggedUserId, new Date().toLocaleDateString(), true, false);
            const reportBase64 = reportResponse?.fileBase64 || reportResponse?.base64 || reportResponse?.value?.fileBase64 || reportResponse?.value?.base64 || null;

            if(!reportBase64) {
                enqueueSnackbar("Errore durante la stampa del report della procedura SOS", { variant: "error" });
                return;
            }

            setPdfViewerTitle("Report Procedura SOS");
            setPdfViewerBase64(reportBase64);
            setShowPdfViewer(true);
        } catch (error) {
            logApi.AddLog(`"Errore durante la stampa del report della procedura SOS per l'azienda: ${aziendaId}, errore: ${error}"`,error.stack,"handlePrintReport","ModalManageSosProcedure");
            enqueueSnackbar("Errore durante la stampa del report della procedura SOS", { variant: "error" });
        }
        finally{
            setIsLoading(false);
        }
    }

    return (
        <Fragment>
            <ModalPdfViewer
                show={showPdfViewer}
                title={pdfViewerTitle}
                base64={pdfViewerBase64}
                onDownload={pdfViewerBase64 ? async () => {
                    const link = document.createElement("a");
                    link.href = `data:application/pdf;base64,${pdfViewerBase64}`;
                    link.download = `${pdfViewerTitle || "report"}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } : null}
                onClose={() => {
                    setShowPdfViewer(false);
                    setPdfViewerBase64(null);
                }}
            />
            <Modal
                key="modal-manage-sos-procedure"
                aria-labelledby="modal-manage-sos-procedure-title"
                aria-describedby="modal-manage-sos-procedure-description"
                open={show}
                onClose={onClose}
                title="Gestione procedure SOS"
                description="In questa sezione puoi gestire le procedure SOS. Puoi aggiungere nuove procedure, modificare quelle esistenti o eliminarle. Assicurati di salvare le modifiche prima di uscire."    
            >
                <Box
                    sx={{
                        ...modalStyleXLarge,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    {isLoading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                bgcolor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 1000,
                                borderRadius: 1,
                            }}
                        >
                            <Loading open={isLoading} />
                        </Box>
                    )}
                    <Stack
                        direction="column"
                        spacing={0}
                        justifyContent="left"
                        alignItems="left"
                        sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
                    >                        
                        <ExtendedTypography
                            variant="titleSmall"
                            color="primary.main"
                            sx={{ p: 2, pl: 3 }}
                        >
                            Gestione procedura SOS
                        </ExtendedTypography>
                        <Divider orientation="horizontal" flexItem />   
                        <Stack sx={{ p: 2, flexShrink: 0 }}>
                            <TextField
                                label="Nominativo referente SOS"
                                fullWidth
                                size="small"
                                id="txtNominativoReferenteSos"
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.nominativoReferenteSos || ""}
                                onChange={handleChangeFields}                                    
                            />                     
                        </Stack>
                        <Box
                            sx={{
                                p: 2,
                                flex: 1,
                                minHeight: 0,
                                overflowY: "auto",
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "repeat(2, minmax(0, 1fr))",
                                },
                                columnGap: 1,
                                rowGap: 0.2,
                                alignItems: "start",
                            }}
                        >                            
                            <TextField
                                label="1. Contributo di dipendenti, collaboratori e strutture di contatto"
                                fullWidth
                                size="small"
                                id="txtContributoColleghi"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.contributoColleghi || ""}  
                                onChange={handleChangeFields}                              
                            />        
                            <TextField
                                label="2. Attività e strumenti per l’individuazione e l’esame delle anomalie"
                                fullWidth
                                size="small"
                                id="txtAttivitaStrumentiAnomalie"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.attivitaStrumentiAnomalie || ""}  
                                onChange={handleChangeFields}                              
                            />   
                            <TextField
                                label="3. Criteri di priorità per l’esame delle anomalie"
                                fullWidth
                                size="small"
                                id="txtPrioritaAnomalie"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.prioritaAnomalie || ""}    
                                onChange={handleChangeFields}                           
                            />      
                            <TextField
                                label="4. Strumenti di selezione basati su regole e criteri quantitativi/qualitativi"
                                fullWidth
                                size="small"
                                id="txtStrumentiSelezione"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.strumentiSelezione || ""}   
                                onChange={handleChangeFields}                             
                            />   
                            <TextField
                                label="5. Partenariati per la condivisione delle informazioni"
                                fullWidth
                                size="small"
                                id="txtPartenariati"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.partenariati || ""}   
                                onChange={handleChangeFields}                             
                            />   
                            <TextField
                                label="6. Soggetti esterni per le attività di supporto alla SOS"
                                fullWidth
                                size="small"
                                id="txtSoggettiEsterni"
                                multiline
                                rows={4}
                                inputProps={{ autocomplete: "nope"}}
                                defaultValue={procedureSos?.soggettiEsterni || ""}  
                                onChange={handleChangeFields}                              
                            />
                        </Box>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ p: 1, mt: "auto", bgcolor: "#F2F5F8", flexShrink: 0 }}
                        justifyContent="right"
                        alignItems="right"
                    >
                        <Button variant="outlined" size="small" onClick={handleModalClose}>
                            {t("actions.annulla")}
                        </Button>
                        <Tooltip
                            title={!isUserReferenteSos ? "Per salvare o aggiornare la procedura SOS è necessario essere un referente SOS" : ""}
                            placement="top"
                            arrow
                        >
                            <span>
                                <Button variant="contained" size="small" onClick={handleSave} disabled={!isUserReferenteSos}>
                                    { procedureSos && procedureSos?.id ? t("actions.aggiorna") : t("actions.salva")}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant='contained'
                            size='small'
                            endIcon={<PrintIcon />}
                            onClick={handlePrintReport}  
                            disabled={ procedureSos && procedureSos?.id === 0 }                  
                        >
                            {t("actions.stampa")}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </Fragment>
    )
}